import type { ContractSide, PricePoint } from '../../domain/types'
import { clamp } from './statistics'
import { decideAction, type EvDecision } from './ev'

export function centsToImplied(cents: number): number {
  return clamp(cents / 100, 0, 1)
}

export function noAskCents(yesBidCents: number): number {
  return clamp(100 - yesBidCents, 1, 99)
}

export function noBidCents(yesAskCents: number): number {
  return clamp(100 - yesAskCents, 1, 99)
}

export function spreadCents(yesBidCents: number, yesAskCents: number): number {
  return Math.max(0, yesAskCents - yesBidCents)
}

export type PmFeeModel = 'kalshi' | 'none' | { flatCentsPerContract: number }

// Kalshi-style trading fee: round up 0.07 x contracts x price x (1 - price), in cents.
export function kalshiFeeCents(priceCents: number, contracts = 1): number {
  const p = clamp(priceCents / 100, 0, 1)
  return Math.ceil(7 * contracts * p * (1 - p))
}

export function tradingFeeCents(priceCents: number, contracts: number, model: PmFeeModel = 'kalshi'): number {
  if (model === 'none') return 0
  if (typeof model === 'object') return Math.max(0, Math.round(model.flatCentsPerContract * contracts))
  return kalshiFeeCents(priceCents, contracts)
}

export interface ContractEvInput {
  side: ContractSide
  modelProb: number
  yesBidCents: number
  yesAskCents: number
  contracts?: number
  feeModel?: PmFeeModel
  minEdge?: number
}

export interface ContractEv {
  side: ContractSide
  entryPriceCents: number
  feeCents: number
  evCents: number
  evPerDollar: number
  edge: number
  breakEvenProb: number
  payoutIfWinCents: number
  lossIfLoseCents: number
  winProb: number
  decision: EvDecision
}

export function contractEv(input: ContractEvInput): ContractEv {
  const contracts = input.contracts ?? 1
  const feeModel = input.feeModel ?? 'kalshi'
  const minEdge = input.minEdge ?? 0.02
  const p = clamp(input.modelProb, 0, 1)
  const entry = input.side === 'yes' ? input.yesAskCents : noAskCents(input.yesBidCents)
  const winProb = input.side === 'yes' ? p : 1 - p
  const totalFee = tradingFeeCents(entry, contracts, feeModel)
  const feePer = contracts > 0 ? totalFee / contracts : 0

  const evCents = winProb * 100 * contracts - entry * contracts - totalFee
  const evPerDollar = entry > 0 && contracts > 0 ? evCents / (entry * contracts) : 0
  const breakEvenProb = clamp((entry + feePer) / 100, 0, 1)
  const edge = winProb - breakEvenProb

  return {
    side: input.side,
    entryPriceCents: entry,
    feeCents: totalFee,
    evCents,
    evPerDollar,
    edge,
    breakEvenProb,
    payoutIfWinCents: (100 - entry) * contracts - totalFee,
    lossIfLoseCents: entry * contracts + totalFee,
    winProb,
    decision: decideAction(edge, evPerDollar, minEdge),
  }
}

export function bestSide(modelProb: number, yesBidCents: number, yesAskCents: number, feeModel: PmFeeModel = 'kalshi', minEdge = 0.02): ContractEv {
  const yes = contractEv({ side: 'yes', modelProb, yesBidCents, yesAskCents, feeModel, minEdge })
  const no = contractEv({ side: 'no', modelProb, yesBidCents, yesAskCents, feeModel, minEdge })
  return no.evPerDollar > yes.evPerDollar ? no : yes
}

// "What price would make this worth it?" — the highest entry price for which EV/$ still clears the edge bar.
export function requiredEntryPriceCents(modelProb: number, side: ContractSide, minEdge = 0, feeModel: PmFeeModel = 'kalshi'): number {
  const winProb = side === 'yes' ? clamp(modelProb, 0, 1) : 1 - clamp(modelProb, 0, 1)
  for (let entry = 99; entry >= 1; entry--) {
    const fee = tradingFeeCents(entry, 1, feeModel)
    const evPerDollar = (winProb * 100 - entry - fee) / entry
    if (evPerDollar >= minEdge) return entry
  }
  return 0
}

export interface ExitInput {
  side: ContractSide
  entryPriceCents: number
  contracts: number
  yesBidCents: number
  yesAskCents: number
  feeModel?: PmFeeModel
}

export interface ExitResult {
  currentSidePriceCents: number
  currentExitValueCents: number
  entryCostCents: number
  markToMarketPnlCents: number
  pnlIfWinCents: number
  pnlIfLoseCents: number
}

export function exitCalculator(input: ExitInput): ExitResult {
  const feeModel = input.feeModel ?? 'kalshi'
  const sidePrice = input.side === 'yes' ? input.yesBidCents : noBidCents(input.yesAskCents)
  const entryFee = tradingFeeCents(input.entryPriceCents, input.contracts, feeModel)
  const exitFee = tradingFeeCents(sidePrice, input.contracts, feeModel)
  const entryCost = input.entryPriceCents * input.contracts + entryFee
  const exitValue = sidePrice * input.contracts - exitFee

  return {
    currentSidePriceCents: sidePrice,
    currentExitValueCents: exitValue,
    entryCostCents: entryCost,
    markToMarketPnlCents: exitValue - entryCost,
    pnlIfWinCents: (100 - input.entryPriceCents) * input.contracts - entryFee,
    pnlIfLoseCents: -input.entryPriceCents * input.contracts - entryFee,
  }
}

export interface MultiOutcomeLeg {
  id: string
  title: string
  yesCents: number
}

export interface MultiOutcomeResult {
  sumYesCents: number
  overroundPct: number
  arbitrage: boolean
  legs: Array<{ id: string; title: string; rawProb: number; fairProb: number }>
}

// For a mutually-exclusive event, YES prices should sum to ~100. The deviation is the vig (or an arb).
export function multiOutcome(contracts: readonly MultiOutcomeLeg[]): MultiOutcomeResult {
  const sumYesCents = contracts.reduce((acc, c) => acc + c.yesCents, 0)
  const overroundPct = (sumYesCents / 100 - 1) * 100
  return {
    sumYesCents,
    overroundPct,
    arbitrage: sumYesCents < 100 && contracts.length >= 2,
    legs: contracts.map((c) => ({
      id: c.id,
      title: c.title,
      rawProb: centsToImplied(c.yesCents),
      fairProb: sumYesCents > 0 ? c.yesCents / sumYesCents : 0,
    })),
  }
}

export function liquidityScore(volume: number, spread: number): number {
  const volumeScore = clamp(Math.log10(volume + 1) / 5, 0, 1)
  const spreadScore = clamp(1 - spread / 15, 0, 1)
  return clamp(0.6 * volumeScore + 0.4 * spreadScore, 0, 1)
}

export function slippageEstimateCents(liquidity: number): number {
  return Math.round((1 - clamp(liquidity, 0, 1)) * 5)
}

export function impliedProbabilitySeries(history: readonly PricePoint[]): Array<{ time: string; prob: number }> {
  return history.map((h) => ({ time: h.time, prob: centsToImplied(h.yesPriceCents) }))
}

export type MovementDirection = 'up' | 'down' | 'flat'

export interface MovementAlert {
  changeCents: number
  changePct: number
  direction: MovementDirection
  significant: boolean
  message: string
}

export function movementAlert(history: readonly PricePoint[], opts: { thresholdCents?: number; lookback?: number } = {}): MovementAlert {
  const threshold = opts.thresholdCents ?? 5
  if (history.length < 2) return { changeCents: 0, changePct: 0, direction: 'flat', significant: false, message: 'No price history yet.' }
  const last = history[history.length - 1].yesPriceCents
  const lookbackIdx = opts.lookback !== undefined ? Math.max(0, history.length - 1 - opts.lookback) : 0
  const base = history[lookbackIdx].yesPriceCents
  const changeCents = last - base
  const changePct = base > 0 ? (changeCents / base) * 100 : 0
  const direction: MovementDirection = changeCents > 0 ? 'up' : changeCents < 0 ? 'down' : 'flat'
  const significant = Math.abs(changeCents) >= threshold
  const message = significant
    ? `Moved ${changeCents > 0 ? '+' : ''}${changeCents}\u00A2 (${changePct.toFixed(0)}%) ${direction === 'up' ? 'up' : 'down'} — implied probability is shifting.`
    : 'Stable — no significant move.'
  return { changeCents, changePct, direction, significant, message }
}

export function crossedTarget(currentAskCents: number, targetCents: number): boolean {
  return currentAskCents <= targetCents
}

export interface ChecklistItem {
  id: string
  question: string
}

export const EVENT_RESOLUTION_CHECKLIST: ChecklistItem[] = [
  { id: 'criteria', question: 'Can I state the exact resolution criteria in one sentence?' },
  { id: 'source', question: 'Do I know the official source that decides the outcome?' },
  { id: 'date', question: 'Do I know the exact resolution date and time?' },
  { id: 'edge-cases', question: 'Have I considered ambiguous or edge-case outcomes?' },
  { id: 'settlement-risk', question: 'Do I understand settlement and counterparty risk?' },
  { id: 'liquidity', question: 'Is there enough liquidity to exit before resolution?' },
]

export function resolutionReady(answers: Record<string, boolean>): { ready: boolean; missing: string[] } {
  const missing = EVENT_RESOLUTION_CHECKLIST.filter((c) => !answers[c.id]).map((c) => c.id)
  return { ready: missing.length === 0, missing }
}
